import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Download } from "lucide-react";
import config from "../config";
import logo_img from "../public/img/LT_logo.svg";
import lightbulb from "../public/img/lightbulb.svg";
import { generatePdf } from "../lib/generatePdf";
import "./PublicWebsiteReport.css";

const STAGES = [
  { key: "http_analysis", label: "HTTP Analysis" },
  { key: "ssl_quick", label: "SSL/TLS Certificate" },
  { key: "scrape", label: "Page Content Scrape" },
  { key: "robots_sitemap", label: "Robots & Sitemap" },
  { key: "structured_data", label: "Structured Data Validation" },
  { key: "social_meta", label: "Social Meta Analysis" },
  { key: "broken_links", label: "Broken Link Check" },
  { key: "readability", label: "Readability Analysis" },
  { key: "pagespeed", label: "PageSpeed Insights" },
  { key: "mobile_friendliness", label: "Mobile-Friendliness" },
  { key: "crux", label: "Chrome UX Report" },
  { key: "domain_info", label: "Domain Information" },
  { key: "screenshot", label: "Website Screenshot" },
  { key: "technology", label: "Technology Detection" },
  { key: "accessibility", label: "Accessibility Analysis" },
  { key: "privacy_compliance", label: "Privacy Compliance" },
  { key: "software_versions", label: "Software Version Check" },
  { key: "security_headers", label: "Security Headers" },
  { key: "image_optimization", label: "Image Optimization" },
  { key: "js_vulnerabilities", label: "JS Library Vulnerabilities" },
  { key: "http_methods", label: "Exposed HTTP Methods" },
  { key: "url_reputation", label: "URL Reputation" },
  { key: "domain_issues", label: "Domain Issues Analysis" },
  { key: "ai_analysis", label: "AI Analysis" },
];

const PwrNavbar = () => {
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    fetch(`${config.api.baseUrl}${config.api.endpoints.auth.checkAuth}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.authenticated) setIsAuthed(true);
      })
      .catch(() => {});
  }, []);

  return (
    <nav className="pwr-navbar">
      <div className="pwr-navbar-inner">
        <a href={isAuthed ? "/" : "https://www.lt.agency"} className="pwr-navbar-logo-link">
          <img src={logo_img} alt="LT Logo" className="pwr-navbar-logo" />
        </a>
        <a
          href="https://www.lt.agency/contact"
          target="_blank"
          rel="noopener noreferrer"
          className="pwr-navbar-cta"
        >
          <img src={lightbulb} alt="" className="pwr-navbar-cta-icon" />
          Get started here!
        </a>
      </div>
    </nav>
  );
};

const PublicWebsiteReport = () => {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Theme detection (standalone — same logic as App.js)
  useEffect(() => {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };
    const getSystemPreference = () =>
      window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
    const applyTheme = (isDark) =>
      document.documentElement.setAttribute(
        "data-theme",
        isDark ? "dark" : "light"
      );

    const manualTheme = getCookie("theme-manual");
    if (manualTheme) {
      applyTheme(manualTheme === "dark");
    } else {
      applyTheme(getSystemPreference());
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e) => {
        if (!getCookie("theme-manual")) applyTheme(e.matches);
      };
      mq.addEventListener?.("change", handler);
      return () => mq.removeEventListener?.("change", handler);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      const resp = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.websiteReport.reports}/${token}`
      );
      const data = await resp.json();
      if (data.success) {
        setReport(data.report);
      } else {
        setError(data.error || "Report not found");
      }
    } catch (err) {
      setError("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Poll while running
  useEffect(() => {
    if (!report || report.status !== "running") return;
    const interval = setInterval(fetchReport, 5000);
    return () => clearInterval(interval);
  }, [report, fetchReport]);

  if (loading) {
    return (
      <div className="pwr-page">
        <PwrNavbar />
        <div className="pwr-loading">
          <div className="pwr-spinner" />
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pwr-page">
        <PwrNavbar />
        <div className="pwr-error-page">
          <h1>Report Not Found</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (report.status === "running") {
    return <RunningView report={report} />;
  }

  return <CompletedReport report={report} />;
};

/* ======================================================================== */
/* Running Progress View                                                     */
/* ======================================================================== */
const RunningView = ({ report }) => {
  const progress = report.progress || {};
  const stagesDone = new Set(progress.stages_done || []);
  const enabledSet = new Set(report.enabled_stages || STAGES.map((s) => s.key));
  const activeStages = STAGES.filter((s) => enabledSet.has(s.key));
  const total = progress.stages_total || activeStages.length;
  const pct = ((progress.stages_completed || 0) / total) * 100;

  return (
    <div className="pwr-page">
      <PwrNavbar />
      <div className="pwr-running-container">
        <h1 className="pwr-running-domain">{report.domain}</h1>
        <p className="pwr-running-subtitle">Analyzing website...</p>

        <div className="pwr-running-progress-bar">
          <div className="pwr-running-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <p className="pwr-running-stage">
          {progress.current_stage_label || "Starting..."}
          <span className="pwr-running-count">
            {" "}({progress.stages_completed || 0}/{total})
          </span>
        </p>

        <div className="pwr-stage-checklist">
          {activeStages.map(({ key, label }) => (
            <div
              key={key}
              className={`pwr-stage-item ${
                stagesDone.has(key)
                  ? "pwr-stage-done"
                  : progress.current_stage === key
                    ? "pwr-stage-active"
                    : "pwr-stage-pending"
              }`}
            >
              <span className="pwr-stage-icon">
                {stagesDone.has(key) ? "\u2713" : progress.current_stage === key ? "\u25CB" : "\u2022"}
              </span>
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ======================================================================== */
/* Completed Report View                                                     */
/* ======================================================================== */
const CompletedReport = ({ report }) => {
  const sections = report.sections || {};
  const aiData = sections.ai_analysis?.data || {};
  const sectionInsights = report.section_insights || {};
  const executiveTitle = report.executive_title || "Executive Summary";
  const verdict = report.verdict || aiData.verdict;
  const securitySummary = report.security_summary || aiData.security_summary;

  const [isGenerating, setIsGenerating] = useState(false);
  const autoDownloadFiredRef = useRef(false);

  const runDownload = useCallback(async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      await generatePdf(report);
    } catch (err) {
      console.error("PDF generation failed:", err);
      window.alert(
        "PDF generation failed. The report may be too large for browser-based PDF export. Please try again or contact support."
      );
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, report]);

  useEffect(() => {
    if (autoDownloadFiredRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("download") !== "1") return;
    autoDownloadFiredRef.current = true;
    // Defer one paint so layout is settled and screenshots have started loading.
    const t = setTimeout(() => {
      runDownload();
    }, 500);
    return () => clearTimeout(t);
  }, [runDownload]);

  // Normalize category_scores — handle both old format (number) and new format ({score, description})
  const rawCatScores = aiData.category_scores || {};
  const categoryScores = {};
  for (const [cat, val] of Object.entries(rawCatScores)) {
    if (typeof val === "number") {
      categoryScores[cat] = { score: val, description: "" };
    } else if (val && typeof val === "object") {
      categoryScores[cat] = { score: val.score, description: val.description || "" };
    }
  }

  return (
    <div className="pwr-page">
      <PwrNavbar />
      <div className="pwr-report" id="pwr-report-root">
        {/* 1. Header */}
        <header className="pwr-header">
          <div className="pwr-header-inner">
            <button
              type="button"
              className="pwr-header-download pwr-no-print"
              onClick={runDownload}
              disabled={isGenerating}
              aria-label="Download full report as PDF"
            >
              <Download size={16} strokeWidth={2} />
              <span>{isGenerating ? "Generating..." : "Download Full Report"}</span>
            </button>
            <div className="pwr-header-domain">{report.domain?.toUpperCase()}</div>
            <div className="pwr-header-grade-row">
              {report.overall_grade && (
                <div className={`pwr-grade-circle ${gradeColorClass(report.overall_grade)}`}>
                  {report.overall_grade}
                </div>
              )}
              {report.overall_score != null && (
                <div className="pwr-header-score">{report.overall_score}/100</div>
              )}
            </div>
            {verdict && (
              <p className="pwr-header-verdict">{verdict}</p>
            )}
            <div className="pwr-header-date">
              Generated{" "}
              {report.completed_at
                ? new Date(report.completed_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : ""}
            </div>
          </div>
        </header>

        {/* 2. Executive Summary */}
        {report.executive_summary && (
          <Section title={executiveTitle}>
            <p className="pwr-executive-summary">{report.executive_summary}</p>
          </Section>
        )}

        {/* 3. Category Scores */}
        {Object.keys(categoryScores).length > 0 && (
          <Section title="At a Glance">
            <div className="pwr-category-grid">
              {Object.entries(categoryScores).map(([cat, { score, description }]) => (
                <div key={cat} className="pwr-category-card">
                  <ScoreGauge score={score} size={80} />
                  <div className="pwr-category-label" style={{ color: scoreColor(score) }}>{cat}</div>
                  {description && (
                    <div className="pwr-category-desc">{description}</div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* 4. Screenshots */}
        <RenderScreenshot data={sections.screenshot} insight={sectionInsights.screenshot} />

        {/* 5. Speed & Performance */}
        <RenderSpeedPerformance
          pagespeed={sections.pagespeed}
          insight={sectionInsights.pagespeed}
        />

        {/* 6. Content Quality */}
        <RenderContentQuality
          data={sections.readability}
          insight={sectionInsights.readability}
        />

        {/* 7. Search Engine Optimization */}
        <RenderSEO
          scrape={sections.scrape}
          socialMeta={sections.social_meta}
          structuredData={sections.structured_data}
          insight={sectionInsights.scrape}
        />

        {/* 8. Mobile Experience */}
        <RenderMobileExperience
          data={sections.mobile_friendliness}
          insight={sectionInsights.mobile_friendliness}
        />

        {/* 9. Accessibility & Compliance */}
        <RenderAccessibilityCompliance
          accessibility={sections.accessibility}
          privacy={sections.privacy_compliance}
          accessibilityInsight={sectionInsights.accessibility}
          privacyInsight={sectionInsights.privacy_compliance}
        />

        {/* 10. Security Posture */}
        <RenderSecurityPosture
          ssl={sections.ssl_quick}
          securityHeaders={sections.security_headers}
          softwareVersions={sections.software_versions}
          jsVulnerabilities={sections.js_vulnerabilities}
          securitySummary={securitySummary}
        />

        {/* 11. Issues Found */}
        <RenderIssuesFound
          brokenLinks={sections.broken_links}
          imageOptimization={sections.image_optimization}
          brokenLinksInsight={sectionInsights.broken_links}
          imageInsight={sectionInsights.image_optimization}
        />

        {/* 12. Recommendations */}
        <RenderRecommendations data={aiData} />

        {/* 13. Technical Appendix */}
        <div data-pdf-strip="appendix">
          <TechnicalAppendix sections={sections} sectionInsights={sectionInsights} />
        </div>

        {/* 14. Footer */}
        <footer className="pwr-footer">
          <div className="pwr-footer-inner">
            <img src={logo_img} alt="LT Agency" className="pwr-footer-logo" />
            <p>Prepared by <a href="https://www.lt.agency" target="_blank" rel="noopener noreferrer">LT</a></p>
            <a
              href="https://www.lt.agency/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="pwr-footer-cta"
            >
              Ready to discuss these findings? Get in touch.
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

/* ======================================================================== */
/* Helper Components                                                         */
/* ======================================================================== */

const Section = ({ title, children }) => (
  <section className="pwr-section">
    <div className="pwr-section-inner">
      <h2 className="pwr-section-title">{title}</h2>
      {children}
    </div>
  </section>
);

const CollapsibleSection = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="pwr-section pwr-collapsible">
      <div className="pwr-section-inner">
        <button
          className={`pwr-collapsible-header ${open ? "pwr-collapsible-open" : ""}`}
          onClick={() => setOpen(!open)}
        >
          <h2 className="pwr-section-title">{title}</h2>
          <span className="pwr-collapsible-arrow">{open ? "\u25B2" : "\u25BC"}</span>
        </button>
        {open && <div className="pwr-collapsible-body">{children}</div>}
      </div>
    </section>
  );
};

const SectionWrapper = ({ title, sectionData, children }) => {
  if (!sectionData) return null;
  const data = sectionData.data;
  if (!data) return null;
  if (data.skipped)
    return (
      <Section title={title}>
        <p className="pwr-skipped">{data.reason || "Skipped"}</p>
      </Section>
    );
  if (data.error)
    return (
      <Section title={title}>
        <p className="pwr-section-error">
          Unable to gather this data: {data.error_message || "Unknown error"}
        </p>
      </Section>
    );
  return <Section title={title}>{children(data)}</Section>;
};

function gradeColorClass(grade) {
  if (!grade) return "";
  const l = grade.charAt(0).toUpperCase();
  if (l === "A") return "pwr-grade-green";
  if (l === "B") return "pwr-grade-yellow-green";
  if (l === "C") return "pwr-grade-orange";
  return "pwr-grade-red";
}

function scoreColor(score) {
  if (score >= 90) return "#22c55e";
  if (score >= 70) return "#eab308";
  if (score >= 50) return "#f97316";
  return "#ef4444";
}

const ScoreGauge = ({ score, size = 80 }) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = circ - (clamped / 100) * circ;
  const color = scoreColor(clamped);

  return (
    <svg width={size} height={size} className="pwr-gauge">
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="var(--pwr-gauge-track)" strokeWidth="6"
      />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x={size / 2} y={size / 2}
        textAnchor="middle" dominantBaseline="central"
        className="pwr-gauge-text"
        fill="var(--pwr-text-primary)"
        fontSize={size > 60 ? "1rem" : "0.75rem"}
        fontWeight="700"
      >
        {Math.round(score)}
      </text>
    </svg>
  );
};

const DataRow = ({ label, value }) => {
  if (value === null || value === undefined) return null;
  return (
    <div className="pwr-data-row">
      <span className="pwr-data-label">{label}</span>
      <span className="pwr-data-value">{String(value)}</span>
    </div>
  );
};

const CheckItem = ({ pass, warn, children }) => (
  <div className={`pwr-check-item ${pass ? "pwr-check-pass" : warn ? "pwr-check-warn" : "pwr-check-fail"}`}>
    <span className="pwr-check-icon">{pass ? "\u2713" : warn ? "\u26A0" : "\u2717"}</span>
    <span>{children}</span>
  </div>
);

/* ======================================================================== */
/* Section 4: Screenshots                                                    */
/* ======================================================================== */
const RenderScreenshot = ({ data, insight }) => (
  <SectionWrapper title={insight?.title || "How Your Site Looks"} sectionData={data}>
    {(d) => (
      <div className="pwr-screenshots">
        {d.desktop && (
          <div className="pwr-screenshot-item">
            <h4>Desktop</h4>
            <img src={d.desktop} alt="Desktop screenshot" className="pwr-screenshot-img" />
          </div>
        )}
        {d.mobile && (
          <div className="pwr-screenshot-item pwr-screenshot-mobile">
            <h4>Mobile</h4>
            <img src={d.mobile} alt="Mobile screenshot" className="pwr-screenshot-img" />
          </div>
        )}
      </div>
    )}
  </SectionWrapper>
);

/* ======================================================================== */
/* Section 5: Speed & Performance (simplified PageSpeed)                     */
/* ======================================================================== */
const SpeedPanel = ({ strat, variant, heading }) => {
  if (!strat || strat.error) return null;
  return (
    <div className={`pwr-speed-panel pwr-speed-panel-${variant}`}>
      <h3 className="pwr-speed-panel-heading">{heading}</h3>
      {strat.scores && (
        <div className="pwr-scores-row">
          {Object.entries(strat.scores).map(([k, v]) => (
            <div key={k} className="pwr-score-item">
              <ScoreGauge score={v} size={72} />
              <div className="pwr-score-label">{k.replace(/-/g, " ")}</div>
            </div>
          ))}
        </div>
      )}
      {/* Key metrics — show only the most important ones */}
      {strat.metrics && (
        <div className="pwr-metrics-grid">
          {Object.entries(strat.metrics)
            .filter(([k]) => ["first-contentful-paint", "largest-contentful-paint", "cumulative-layout-shift", "total-blocking-time", "speed-index"].includes(k))
            .map(([k, v]) => (
              <div key={k} className="pwr-metric-card">
                <div className="pwr-metric-value">{v.display || "\u2014"}</div>
                <div className="pwr-metric-label">{k.replace(/-/g, " ")}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const RenderSpeedPerformance = ({ pagespeed, insight }) => {
  const [tab, setTab] = React.useState("mobile");

  if (!pagespeed) return null;
  const d = pagespeed.data;
  if (!d || d.skipped || d.error) return null;

  const mobileOk = d.mobile && !d.mobile.error;
  const desktopOk = d.desktop && !d.desktop.error;
  if (!mobileOk && !desktopOk) return null;

  // If only one strategy is available, force the active tab to it.
  const activeTab = mobileOk && desktopOk ? tab : mobileOk ? "mobile" : "desktop";

  return (
    <Section title={insight?.title || "Speed & Performance"}>
      {insight?.summary && (
        <p className="pwr-section-summary">{insight.summary}</p>
      )}
      {mobileOk && desktopOk && (
        <div className="pwr-tab-row pwr-no-print">
          <button className={`pwr-tab ${activeTab === "mobile" ? "pwr-tab-active" : ""}`} onClick={() => setTab("mobile")}>Mobile</button>
          <button className={`pwr-tab ${activeTab === "desktop" ? "pwr-tab-active" : ""}`} onClick={() => setTab("desktop")}>Desktop</button>
        </div>
      )}
      <div className={`pwr-speed-panels pwr-speed-active-${activeTab}`}>
        {mobileOk && <SpeedPanel strat={d.mobile} variant="mobile" heading="Mobile" />}
        {desktopOk && <SpeedPanel strat={d.desktop} variant="desktop" heading="Desktop" />}
      </div>
    </Section>
  );
};

/* ======================================================================== */
/* Section 6: Content Quality (simplified Readability)                       */
/* ======================================================================== */
const READING_LEVEL_SCALE = [
  { label: "Easy", min: 70, color: "#22c55e" },
  { label: "Average", min: 50, color: "#eab308" },
  { label: "Difficult", min: 30, color: "#f97316" },
  { label: "Very Difficult", min: 0, color: "#ef4444" },
];

function getReadingLevelInfo(fleschScore) {
  for (const level of READING_LEVEL_SCALE) {
    if (fleschScore >= level.min) return level;
  }
  return READING_LEVEL_SCALE[READING_LEVEL_SCALE.length - 1];
}

const RenderContentQuality = ({ data, insight }) => {
  if (!data) return null;
  const d = data.data;
  if (!d || d.skipped || d.error) return null;

  const flesch = d.flesch_reading_ease;
  const readingLevel = d.overall_reading_level;
  const levelInfo = flesch != null ? getReadingLevelInfo(flesch) : null;

  return (
    <Section title={insight?.title || "Content Quality"}>
      {insight?.summary && (
        <p className="pwr-section-summary">{insight.summary}</p>
      )}

      {readingLevel && (
        <div className="pwr-content-reading-level">
          Your content reads at a <strong>{readingLevel}</strong> level
        </div>
      )}

      {/* Visual scale */}
      {flesch != null && (
        <div className="pwr-reading-scale">
          <div className="pwr-reading-scale-bar">
            <div className="pwr-reading-scale-fill" style={{ width: `${Math.max(5, Math.min(100, flesch))}%` }}>
              <span className="pwr-reading-scale-marker" style={{ backgroundColor: levelInfo?.color }}>{Math.round(flesch)}</span>
            </div>
          </div>
          <div className="pwr-reading-scale-labels">
            <span style={{ color: "#ef4444" }}>Difficult</span>
            <span style={{ color: "#eab308" }}>Average</span>
            <span style={{ color: "#22c55e" }}>Easy</span>
          </div>
        </div>
      )}

      <p className="pwr-content-why">
        The recommended reading level for most business websites is <strong>8th-9th grade</strong> (Flesch score of 60-70).
        Simpler language leads to higher engagement, longer time on site, and more conversions — not because your visitors
        aren't smart, but because clear writing helps people make faster decisions.
      </p>
    </Section>
  );
};

/* ======================================================================== */
/* Section 7: SEO (merged scrape + social meta + structured data)            */
/* ======================================================================== */
const RenderSEO = ({ scrape, socialMeta, structuredData, insight }) => {
  if (!scrape) return null;
  const d = scrape.data;
  if (!d || d.skipped || d.error) return null;

  const smData = socialMeta?.data;
  const sdData = structuredData?.data;

  return (
    <Section title={insight?.title || "Search Engine Optimization"}>
      {insight?.summary && (
        <p className="pwr-section-summary">{insight.summary}</p>
      )}

      <div className="pwr-checklist">
        <CheckItem pass={!!d.title && d.title_length >= 30 && d.title_length <= 60} warn={!!d.title && (d.title_length < 30 || d.title_length > 60)}>
          Page Title: {d.title ? `"${d.title.substring(0, 50)}${d.title.length > 50 ? "..." : ""}" (${d.title_length} chars)` : "Missing"}
        </CheckItem>

        <CheckItem pass={!!d.description && d.description_length >= 120 && d.description_length <= 160} warn={!!d.description && (d.description_length < 120 || d.description_length > 160)}>
          Meta Description: {d.description ? `${d.description_length} characters` : "Missing"}
          {d.description_length > 160 ? " (too long, may be truncated)" : ""}
        </CheckItem>

        <CheckItem pass={d.headings?.h1?.count === 1} warn={d.headings?.h1?.count > 1}>
          H1 Heading: {d.headings?.h1?.count === 1 ? "Present" : d.headings?.h1?.count > 1 ? `${d.headings.h1.count} found (should be 1)` : "Missing"}
        </CheckItem>

        <CheckItem pass={d.missing_alt_count === 0} warn={d.missing_alt_count > 0 && d.missing_alt_count <= 3}>
          Image Alt Text: {d.missing_alt_count === 0 ? `All ${d.images_count} images have descriptions` : `${d.missing_alt_count} of ${d.images_count} images missing descriptions`}
        </CheckItem>

        {smData && !smData.skipped && !smData.error && (
          <CheckItem pass={smData.og_score >= 80 && smData.twitter_score >= 80} warn={smData.og_score >= 60 || smData.twitter_score >= 60}>
            Social Sharing Tags: {smData.og_tags_found + smData.twitter_tags_found}/{smData.og_tags_total + smData.twitter_tags_total} present
          </CheckItem>
        )}

        {sdData && !sdData.skipped && !sdData.error && (
          <CheckItem pass={sdData.found && sdData.all_valid} warn={sdData.found && !sdData.all_valid}>
            Structured Data: {sdData.found ? `${sdData.count} schema(s) found${sdData.all_valid ? "" : " with issues"}` : "Not found"}
          </CheckItem>
        )}

        <CheckItem pass={!!d.canonical}>
          Canonical URL: {d.canonical ? "Set" : "Missing"}
        </CheckItem>
      </div>

      <p className="pwr-section-why">
        These elements determine how your site appears in search results and whether search engines can
        properly understand and rank your content. Missing or poorly configured items mean missed opportunities
        for organic traffic.
      </p>
    </Section>
  );
};

/* ======================================================================== */
/* Section 8: Mobile Experience                                              */
/* ======================================================================== */
const RenderMobileExperience = ({ data, insight }) => (
  <SectionWrapper title={insight?.title || "Mobile Experience"} sectionData={data}>
    {(d) => (
      <div>
        {insight?.summary && (
          <p className="pwr-section-summary">{insight.summary}</p>
        )}
        <div className="pwr-checklist" style={{ marginBottom: 20 }}>
          <CheckItem pass={d.viewport_configured}>
            Viewport meta tag configured
          </CheckItem>
        </div>
        {(d.mobile_performance_score != null || d.mobile_seo_score != null) && (
          <div className="pwr-scores-row" style={{ marginBottom: 20 }}>
            {d.mobile_performance_score != null && (
              <div className="pwr-score-item">
                <ScoreGauge score={d.mobile_performance_score} size={72} />
                <div className="pwr-score-label">Mobile Performance</div>
              </div>
            )}
            {d.mobile_seo_score != null && (
              <div className="pwr-score-item">
                <ScoreGauge score={d.mobile_seo_score} size={72} />
                <div className="pwr-score-label">Mobile SEO</div>
              </div>
            )}
            {d.mobile_accessibility_score != null && (
              <div className="pwr-score-item">
                <ScoreGauge score={d.mobile_accessibility_score} size={72} />
                <div className="pwr-score-label">Mobile Accessibility</div>
              </div>
            )}
          </div>
        )}
        {d.issues?.length > 0 && (
          <div className="pwr-issues-list">
            {d.issues.map((issue, i) => (
              <div key={i} className="pwr-issue-item">{issue}</div>
            ))}
          </div>
        )}
      </div>
    )}
  </SectionWrapper>
);

/* ======================================================================== */
/* Section 9: Accessibility & Compliance                                     */
/* ======================================================================== */
const WCAG_BADGE_STYLES = {
  "Level AA Compliant": { className: "pwr-wcag-badge-aa", icon: "\u2713" },
  "Level A Compliant": { className: "pwr-wcag-badge-a", icon: "\u2713" },
  "Not Compliant": { className: "pwr-wcag-badge-fail", icon: "\u2717" },
};

const RenderAccessibilityCompliance = ({ accessibility, privacy, accessibilityInsight, privacyInsight }) => {
  const a11yData = accessibility?.data;
  const privacyData = privacy?.data;
  const hasA11y = a11yData && !a11yData.skipped && !a11yData.error;
  const hasPrivacy = privacyData && !privacyData.skipped && !privacyData.error;

  if (!hasA11y && !hasPrivacy) return null;

  return (
    <Section title={accessibilityInsight?.title || "Accessibility & Compliance"}>
      {/* Accessibility */}
      {hasA11y && (
        <div style={{ marginBottom: hasPrivacy ? 32 : 0 }}>
          {accessibilityInsight?.summary && (
            <p className="pwr-section-summary">{accessibilityInsight.summary}</p>
          )}

          {/* WCAG Badge */}
          {a11yData.wcag_mapping && (
            <div className="pwr-wcag-section">
              <div className={`pwr-wcag-badge ${(WCAG_BADGE_STYLES[a11yData.wcag_mapping.compliance_level] || WCAG_BADGE_STYLES["Not Compliant"]).className}`}>
                <span className="pwr-wcag-badge-icon">
                  {(WCAG_BADGE_STYLES[a11yData.wcag_mapping.compliance_level] || WCAG_BADGE_STYLES["Not Compliant"]).icon}
                </span>
                <span className="pwr-wcag-badge-text">{a11yData.wcag_mapping.compliance_level}</span>
              </div>
              <p className="pwr-wcag-subtitle">
                WCAG 2.1 Assessment — US law (ADA &amp; Section 508) requires Level AA minimum
              </p>
            </div>
          )}

          {/* Summary stats */}
          <div className="pwr-a11y-summary">
            <div className="pwr-a11y-stat pwr-a11y-errors">
              <div className="pwr-a11y-stat-num">{a11yData.errors}</div>
              <div className="pwr-a11y-stat-label">Errors</div>
            </div>
            <div className="pwr-a11y-stat pwr-a11y-alerts">
              <div className="pwr-a11y-stat-num">{a11yData.alerts}</div>
              <div className="pwr-a11y-stat-label">Alerts</div>
            </div>
            <div className="pwr-a11y-stat">
              <div className="pwr-a11y-stat-num">{a11yData.contrast_errors}</div>
              <div className="pwr-a11y-stat-label">Contrast</div>
            </div>
          </div>

          {/* Collapsible detail */}
          {a11yData.details && (
            <CollapsibleDetail label="View accessibility details">
              {["error", "contrast", "alert"].map((catKey) => {
                const cat = a11yData.details[catKey];
                if (!cat || !cat.items || Object.keys(cat.items).length === 0) return null;
                const catLabels = { error: "Errors", contrast: "Contrast Errors", alert: "Alerts" };
                return (
                  <div key={catKey} className="pwr-a11y-detail-group">
                    <h4 className={catKey === "alert" ? "pwr-a11y-cat-alert" : "pwr-a11y-cat-error"}>
                      {catLabels[catKey]} ({cat.count})
                    </h4>
                    <div className="pwr-a11y-detail-list">
                      {Object.entries(cat.items).map(([itemKey, item]) => (
                        <div key={itemKey} className="pwr-a11y-detail-item">
                          <span className="pwr-a11y-detail-count">{item.count}</span>
                          <span className="pwr-a11y-detail-desc">{item.description || itemKey}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CollapsibleDetail>
          )}
        </div>
      )}

      {/* Privacy Compliance */}
      {hasPrivacy && (
        <div>
          <h3 className="pwr-subsection-title">Privacy & Legal Compliance</h3>
          {privacyInsight?.summary && (
            <p className="pwr-section-summary">{privacyInsight.summary}</p>
          )}
          <div className="pwr-checklist">
            <CheckItem pass={privacyData.privacy_policy_linked}>Privacy Policy</CheckItem>
            <CheckItem pass={privacyData.cookie_consent_detected}>Cookie Consent Mechanism</CheckItem>
            <CheckItem pass={privacyData.terms_of_service_linked}>Terms of Service</CheckItem>
          </div>
        </div>
      )}
    </Section>
  );
};

/* ======================================================================== */
/* Section 10: Security Posture (consolidated)                               */
/* ======================================================================== */
const RenderSecurityPosture = ({ ssl, securityHeaders, softwareVersions, jsVulnerabilities, securitySummary }) => {
  const sslData = ssl?.data;
  const shData = securityHeaders?.data;
  const svData = softwareVersions?.data;
  const jsData = jsVulnerabilities?.data;

  const hasSsl = sslData && !sslData.skipped && !sslData.error;
  const hasSH = shData && !shData.skipped && !shData.error;
  const hasSV = svData && !svData.skipped && !svData.error;
  const hasJS = jsData && !jsData.skipped && !jsData.error;

  if (!hasSsl && !hasSH && !hasSV && !hasJS) return null;

  // Count checks
  const checks = [];
  if (hasSsl) checks.push({ label: `SSL Certificate: ${sslData.valid ? "Valid" : "Invalid"}${sslData.days_until_expiry != null && sslData.days_until_expiry < 30 ? ` (expires in ${sslData.days_until_expiry} days!)` : ""}`, pass: sslData.valid, warn: sslData.valid && sslData.days_until_expiry != null && sslData.days_until_expiry < 30 });
  if (hasSH) checks.push({ label: `Security Headers: ${shData.headers_present}/${shData.headers_total} configured`, pass: shData.score_pct >= 80, warn: shData.score_pct >= 50 && shData.score_pct < 80 });
  if (hasSV && svData.detected?.length > 0) {
    const eolCount = svData.detected.filter(s => s.is_eol).length;
    checks.push({ label: eolCount > 0 ? `Outdated Software: ${eolCount} package(s) need updates` : "All detected software is up to date", pass: eolCount === 0, warn: false });
  }
  if (hasJS) checks.push({ label: jsData.has_vulnerabilities ? `Known Vulnerabilities: ${jsData.vulnerable_count} library/libraries with issues` : "No known code vulnerabilities", pass: !jsData.has_vulnerabilities, warn: false });

  const passed = checks.filter(c => c.pass).length;
  const total = checks.length;

  // Use AI-generated summary if available, otherwise compute
  const posture = securitySummary?.posture || (passed === total ? "Strong" : passed >= total * 0.6 ? "Moderate Risk" : "Needs Attention");
  const headline = securitySummary?.headline;

  return (
    <Section title="Security Posture">
      <div className="pwr-security-badge-row">
        <div className={`pwr-security-posture-badge ${posture === "Strong" ? "pwr-posture-strong" : posture === "Moderate Risk" ? "pwr-posture-moderate" : "pwr-posture-weak"}`}>
          {posture}
        </div>
        <span className="pwr-security-score">{passed}/{total} checks passed</span>
      </div>

      {headline && (
        <p className="pwr-section-summary">{headline}</p>
      )}

      <div className="pwr-checklist">
        {checks.map((check, i) => (
          <CheckItem key={i} pass={check.pass} warn={check.warn}>{check.label}</CheckItem>
        ))}
      </div>

      {/* Collapsible technical detail */}
      <CollapsibleDetail label="View full security details">
        {/* SSL detail */}
        {hasSsl && (
          <div className="pwr-tech-detail-block">
            <h4>SSL/TLS Certificate</h4>
            <div className="pwr-data-list">
              <DataRow label="Valid" value={sslData.valid ? "Yes" : "No"} />
              <DataRow label="Issuer" value={sslData.issuer} />
              <DataRow label="Expires" value={sslData.expires} />
              <DataRow label="Days Until Expiry" value={sslData.days_until_expiry} />
              <DataRow label="Protocol" value={sslData.protocol_version} />
            </div>
          </div>
        )}

        {/* Security headers detail */}
        {hasSH && shData.headers && (
          <div className="pwr-tech-detail-block">
            <h4>Security Headers</h4>
            <div className="pwr-checklist">
              {Object.entries(shData.headers).map(([name, info]) => (
                <CheckItem key={name} pass={info.present}>
                  <strong>{name}</strong>
                  <div className="pwr-sec-header-desc">{info.description}</div>
                </CheckItem>
              ))}
            </div>
          </div>
        )}

        {/* Software versions detail */}
        {hasSV && svData.detected?.length > 0 && (
          <div className="pwr-tech-detail-block">
            <h4>Software Versions</h4>
            <div className="pwr-sw-list">
              {svData.detected.map((sw) => (
                <div key={sw.name} className={`pwr-sw-card ${sw.is_eol ? "pwr-sw-eol" : "pwr-sw-ok"}`}>
                  <div className="pwr-sw-card-header">
                    <span className="pwr-sw-name">{sw.name}</span>
                    <span className="pwr-sw-version-num">{sw.detected_version}</span>
                    {sw.is_eol ? (
                      <span className="pwr-sw-badge pwr-sw-badge-eol">End of Life</span>
                    ) : sw.is_eol === false ? (
                      <span className="pwr-sw-badge pwr-sw-badge-ok">Supported</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* JS Vulnerabilities detail */}
        {hasJS && jsData.libraries?.length > 0 && (
          <div className="pwr-tech-detail-block">
            <h4>JavaScript Libraries</h4>
            <div className="pwr-sw-list">
              {jsData.libraries.map((lib) => (
                <div key={lib.name} className={`pwr-sw-card ${lib.vulnerability_count > 0 ? "pwr-sw-eol" : "pwr-sw-ok"}`}>
                  <div className="pwr-sw-card-header">
                    <span className="pwr-sw-name">{lib.name}</span>
                    <span className="pwr-sw-version-num" style={{ opacity: 0.7 }}>v{lib.version}</span>
                    {lib.vulnerability_count > 0 ? (
                      <span className="pwr-sw-badge pwr-sw-badge-eol">{lib.vulnerability_count} CVE(s)</span>
                    ) : (
                      <span className="pwr-sw-badge pwr-sw-badge-ok">No known CVEs</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CollapsibleDetail>
    </Section>
  );
};

/* ======================================================================== */
/* Section 11: Issues Found (broken links + image optimization)              */
/* ======================================================================== */
const RenderIssuesFound = ({ brokenLinks, imageOptimization, brokenLinksInsight, imageInsight }) => {
  const blData = brokenLinks?.data;
  const imgData = imageOptimization?.data;

  const hasBroken = blData && !blData.skipped && !blData.error && (blData.broken_count > 0 || blData.redirect_count > 0);
  const hasImgIssues = imgData && !imgData.skipped && !imgData.error && imgData.has_issues;

  if (!hasBroken && !hasImgIssues) return null;

  return (
    <Section title="Issues Found">
      {hasBroken && (
        <div style={{ marginBottom: hasImgIssues ? 24 : 0 }}>
          <h3 className="pwr-subsection-title">{brokenLinksInsight?.title || "Broken Links"}</h3>
          {brokenLinksInsight?.summary && (
            <p className="pwr-section-summary">{brokenLinksInsight.summary}</p>
          )}
          <div className="pwr-issues-stats">
            <div className="pwr-a11y-stat pwr-a11y-errors">
              <div className="pwr-a11y-stat-num">{blData.broken_count}</div>
              <div className="pwr-a11y-stat-label">Broken</div>
            </div>
            <div className="pwr-a11y-stat pwr-a11y-alerts">
              <div className="pwr-a11y-stat-num">{blData.redirect_count}</div>
              <div className="pwr-a11y-stat-label">Redirects</div>
            </div>
            <div className="pwr-a11y-stat">
              <div className="pwr-a11y-stat-num">{blData.total_checked}</div>
              <div className="pwr-a11y-stat-label">Checked</div>
            </div>
          </div>
          {blData.broken?.length > 0 && (
            <CollapsibleDetail label={`View ${blData.broken.length} broken link(s)`}>
              <div className="pwr-data-list">
                {blData.broken.map((link, i) => (
                  <DataRow key={i} label={String(link.status)} value={link.url} />
                ))}
              </div>
            </CollapsibleDetail>
          )}
        </div>
      )}

      {hasImgIssues && (
        <div>
          <h3 className="pwr-subsection-title">{imageInsight?.title || "Image Optimization"}</h3>
          {imageInsight?.summary && (
            <p className="pwr-section-summary">{imageInsight.summary}</p>
          )}
          <div className="pwr-issues-stats">
            <span className="pwr-issues-stat-pill">{imgData.total_images} images found</span>
            <span className="pwr-issues-stat-pill">{imgData.modern_format_pct}% modern formats</span>
          </div>
        </div>
      )}
    </Section>
  );
};

/* ======================================================================== */
/* Section 12: Recommendations (with priority grouping)                      */
/* ======================================================================== */
const PRIORITY_GROUPS = [
  { key: "critical", label: "Fix Now", className: "pwr-priority-critical" },
  { key: "important", label: "Fix Soon", className: "pwr-priority-important" },
  { key: "optional", label: "Nice to Have", className: "pwr-priority-optional" },
];

const EFFORT_LABELS = {
  quick: "Quick Fix",
  moderate: "Moderate Effort",
  major: "Major Project",
};

const RenderRecommendations = ({ data }) => {
  if (!data?.recommendations?.length) return null;

  // Check if recommendations have the new priority field
  const hasPriority = data.recommendations.some(r => r.priority);

  if (hasPriority) {
    return (
      <Section title="Recommendations">
        {PRIORITY_GROUPS.map(({ key, label, className }) => {
          const recs = data.recommendations.filter(r => r.priority === key);
          if (recs.length === 0) return null;
          return (
            <div key={key} className="pwr-rec-group">
              <h3 className={`pwr-rec-group-title ${className}`}>{label}</h3>
              <div className="pwr-recommendations">
                {recs.map((rec, i) => (
                  <div key={i} className="pwr-rec-card">
                    <div className="pwr-rec-header">
                      <span className="pwr-rec-category">{rec.category}</span>
                      {rec.effort && (
                        <span className={`pwr-rec-effort pwr-effort-${rec.effort}`}>
                          {EFFORT_LABELS[rec.effort] || rec.effort}
                        </span>
                      )}
                    </div>
                    {rec.title && <div className="pwr-rec-title">{rec.title}</div>}
                    <p className="pwr-rec-desc">{rec.description}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </Section>
    );
  }

  // Fallback for old-format recommendations (impact-based)
  return (
    <Section title="Recommendations">
      <div className="pwr-recommendations">
        {data.recommendations.map((rec, i) => (
          <div key={i} className="pwr-rec-card">
            <div className="pwr-rec-header">
              <span className="pwr-rec-category">{rec.category}</span>
              <span className={`pwr-rec-impact pwr-impact-${rec.impact?.toLowerCase()}`}>
                {rec.impact}
              </span>
            </div>
            <p className="pwr-rec-desc">{rec.description}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};

/* ======================================================================== */
/* Collapsible Detail (inline toggle)                                        */
/* ======================================================================== */
const CollapsibleDetail = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`pwr-collapsible-detail ${open ? "pwr-collapsible-detail-open" : ""}`}>
      <button className="pwr-collapsible-detail-btn" onClick={() => setOpen(!open)}>
        {open ? "Hide details" : label} {open ? "\u25B2" : "\u25BC"}
      </button>
      <div className="pwr-collapsible-detail-body">{children}</div>
    </div>
  );
};

/* ======================================================================== */
/* Section 13: Technical Appendix                                            */
/* ======================================================================== */
const TechnicalAppendix = ({ sections, sectionInsights }) => {
  const appendixSections = [
    { key: "http_analysis", label: "HTTP Analysis", render: renderHttpAppendix },
    { key: "crux", label: "Chrome UX Report (Real User Data)", render: renderCruxAppendix },
    { key: "robots_sitemap", label: "Robots & Sitemap", render: renderRobotsAppendix },
    { key: "structured_data", label: "Structured Data", render: renderStructuredDataAppendix },
    { key: "social_meta", label: "Social Sharing Meta", render: renderSocialMetaAppendix },
    { key: "technology", label: "Technology Stack", render: renderTechnologyAppendix },
  ];

  // Only show appendix if at least one section has data
  const hasSections = appendixSections.some(({ key }) => {
    const s = sections[key];
    return s && s.data && !s.data.skipped && !s.data.error;
  });

  if (!hasSections) return null;

  return (
    <CollapsibleSection title="Technical Details">
      {appendixSections.map(({ key, label, render }) => {
        const s = sections[key];
        if (!s || !s.data || s.data.skipped || s.data.error) return null;
        const insight = sectionInsights[key];
        return (
          <div key={key} className="pwr-appendix-section">
            <h3 className="pwr-appendix-title">{insight?.title || label}</h3>
            {insight?.summary && (
              <p className="pwr-section-summary">{insight.summary}</p>
            )}
            {render(s.data)}
          </div>
        );
      })}
    </CollapsibleSection>
  );
};

/* -- Appendix renderers -- */

function renderHttpAppendix(d) {
  return (
    <div className="pwr-data-list">
      <DataRow label="Response Time" value={d.response_time_ms ? `${d.response_time_ms}ms` : null} />
      <DataRow label="Status Code" value={d.status_code} />
      <DataRow label="Server" value={d.server} />
      <DataRow label="Content Type" value={d.content_type} />
      <DataRow label="Compression" value={d.compression} />
      <DataRow label="Cache Control" value={d.cache_control} />
      <DataRow label="X-Powered-By" value={d.x_powered_by} />
      <DataRow label="Cookies" value={d.cookie_count} />
      {d.redirect_chain?.length > 0 && (
        <div className="pwr-redirect-chain">
          <span className="pwr-data-label">Redirect Chain</span>
          {d.redirect_chain.map((r, i) => (
            <div key={i} className="pwr-redirect-step">{r.status_code} &rarr; {r.url}</div>
          ))}
        </div>
      )}
    </div>
  );
}

const CWV_THRESHOLDS = {
  largest_contentful_paint: { good: 2500, poor: 4000, unit: "ms", label: "LCP" },
  interaction_to_next_paint: { good: 200, poor: 500, unit: "ms", label: "INP" },
  cumulative_layout_shift: { good: 0.1, poor: 0.25, unit: "", label: "CLS" },
};

function renderCruxAppendix(d) {
  const cwvKeys = new Set(Object.keys(CWV_THRESHOLDS));
  return (
    <div>
      <div className="pwr-cwv-cards">
        {Object.entries(CWV_THRESHOLDS).map(([key, thresholds]) => {
          const metric = d[key];
          const p75 = metric?.p75;
          let color = "var(--pwr-text-muted)";
          let gradeLabel = "No Data";
          if (p75 != null) {
            if (p75 <= thresholds.good) { color = "var(--pwr-green)"; gradeLabel = "Good"; }
            else if (p75 < thresholds.poor) { color = "var(--pwr-yellow)"; gradeLabel = "Needs Improvement"; }
            else { color = "var(--pwr-red)"; gradeLabel = "Poor"; }
          }
          return (
            <div key={key} className="pwr-cwv-card" style={{ borderTopColor: color }}>
              <div className="pwr-cwv-card-label">{thresholds.label}</div>
              <div className="pwr-cwv-card-value" style={{ color }}>
                {p75 != null ? `${p75}${thresholds.unit}` : "No Data"}
              </div>
              <div className="pwr-cwv-card-grade" style={{ color }}>{gradeLabel}</div>
            </div>
          );
        })}
      </div>
      <div className="pwr-crux-grid" style={{ marginTop: 16 }}>
        {Object.entries(d)
          .filter(([key, val]) => !cwvKeys.has(key) && val && (val.p75 != null || val.histogram))
          .map(([metric, val]) => (
            <div key={metric} className="pwr-crux-card">
              <h4 className="pwr-crux-metric-name">{metric.replace(/_/g, " ")}</h4>
              {val.p75 != null && <div className="pwr-crux-p75">P75: {val.p75}</div>}
              {val.histogram && (
                <div className="pwr-crux-bar">
                  {val.histogram.map((bin, i) => {
                    const pct = ((bin.density || 0) * 100).toFixed(1);
                    const colors = ["#22c55e", "#eab308", "#ef4444"];
                    return (
                      <div key={i} className="pwr-crux-bar-segment" style={{ width: `${pct}%`, background: colors[i] || "#999" }} title={`${pct}%`} />
                    );
                  })}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

const CRAWLER_CATEGORY_LABELS = {
  search: "Search Engine Crawlers",
  ai_search: "AI Search & Answer Engine Crawlers",
  ai_training: "AI Training & Content-Use Crawlers",
  seo_tool: "SEO Tool Crawlers",
};

function renderRobotsAppendix(d) {
  return (
    <div>
      <div className="pwr-data-list" style={{ marginBottom: 20 }}>
        <DataRow label="Robots.txt" value={d.robots?.exists ? "Found" : "Not Found"} />
        {d.robots?.exists && (
          <>
            <DataRow label="Disallow Rules" value={d.robots.disallow_count} />
            <DataRow label="Allow Rules" value={d.robots.allow_count} />
            <DataRow label="Sitemap References" value={d.robots.sitemap_references?.length || 0} />
          </>
        )}
        {d.sitemap && (
          <>
            <DataRow label="Sitemap" value={d.sitemap.exists ? "Found" : "Not Found"} />
            {d.sitemap.exists && <DataRow label="URLs in Sitemap" value={d.sitemap.url_count} />}
          </>
        )}
      </div>

      {d.robots?.blocked_crawlers?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ color: "var(--pwr-red)" }}>Blocked Crawlers</h4>
          <div className="pwr-robots-blocked-list">
            {d.robots.blocked_crawlers.map((c) => (
              <div key={c.name} className="pwr-robots-crawler-card pwr-robots-blocked">
                <div className="pwr-robots-crawler-card-header">
                  <span className="pwr-check-icon" style={{ color: "var(--pwr-red)" }}>{"\u2717"}</span>
                  <strong>{c.name}</strong>
                  <span className="pwr-robots-crawler-company">{c.company}</span>
                </div>
                <p className="pwr-robots-crawler-desc">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {d.robots?.missing_crawlers && ["search", "ai_search", "ai_training", "seo_tool"].map((cat) => {
        const crawlers = d.robots.missing_crawlers[cat];
        if (!crawlers || crawlers.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: 16 }}>
            <h4>{CRAWLER_CATEGORY_LABELS[cat]} Not Addressed</h4>
            <div className="pwr-robots-crawler-grid">
              {crawlers.map((c) => (
                <div key={c.name} className="pwr-robots-crawler-card">
                  <div className="pwr-robots-crawler-card-header">
                    <span className="pwr-robots-crawler-name">{c.name}</span>
                    <span className="pwr-robots-crawler-company">{c.company}</span>
                  </div>
                  <p className="pwr-robots-crawler-desc">{c.description}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderStructuredDataAppendix(d) {
  if (!d.found) return <p className="pwr-text-muted">No structured data found</p>;
  return (
    <div>
      <p className="pwr-text-muted" style={{ marginBottom: 16 }}>
        {d.count} schema(s) found {d.all_valid ? " — all valid" : ""}
      </p>
      <div className="pwr-structured-data-list">
        {d.schemas?.map((s, i) => (
          <div key={i} className="pwr-sd-card">
            <div className="pwr-sd-header">
              <span className="pwr-sd-type">{s.type || "Unknown"}</span>
              {s.issues.length === 0 ? (
                <span className="pwr-sd-status pwr-sd-valid">Valid</span>
              ) : (
                <span className="pwr-sd-status pwr-sd-issues">{s.issues.length} issue(s)</span>
              )}
            </div>
            {s.issues.length > 0 && (
              <div className="pwr-sd-issues-list">
                {s.issues.map((issue, j) => (
                  <div key={j} className="pwr-sd-issue">{issue}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function renderSocialMetaAppendix(d) {
  return (
    <div>
      <div className="pwr-social-scores">
        <div className="pwr-social-score-card">
          <ScoreGauge score={d.og_score} size={72} />
          <div className="pwr-social-score-label">Open Graph ({d.og_tags_found}/{d.og_tags_total})</div>
        </div>
        <div className="pwr-social-score-card">
          <ScoreGauge score={d.twitter_score} size={72} />
          <div className="pwr-social-score-label">Twitter Card ({d.twitter_tags_found}/{d.twitter_tags_total})</div>
        </div>
      </div>
      {d.og_tags_present && (
        <div style={{ marginTop: 16 }}>
          <h4>Open Graph Tags</h4>
          <div className="pwr-checklist">
            {Object.entries(d.og_tags_present).map(([tag, present]) => (
              <CheckItem key={tag} pass={present}>{tag}</CheckItem>
            ))}
          </div>
        </div>
      )}
      {d.twitter_tags_present && (
        <div style={{ marginTop: 16 }}>
          <h4>Twitter Card Tags</h4>
          <div className="pwr-checklist">
            {Object.entries(d.twitter_tags_present).map(([tag, present]) => (
              <CheckItem key={tag} pass={present}>{tag}</CheckItem>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function renderTechnologyAppendix(d) {
  if (!d.technologies || Object.keys(d.technologies).length === 0) {
    return <p className="pwr-text-muted">No technologies detected</p>;
  }
  return (
    <div className="pwr-tech-groups">
      {Object.entries(d.technologies).map(([category, techs]) => (
        <div key={category} className="pwr-tech-group">
          <h4>{category}</h4>
          <div className="pwr-tech-tags">
            {techs.map((t) => (
              <span key={t} className="pwr-tag">{t}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default PublicWebsiteReport;
